import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { logout, getUser } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { 
  FaSchool, FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, 
  FaSignOutAlt, FaSave, FaUpload, FaCog, FaRobot,
  FaNewspaper, FaPencilAlt, FaMicrophone, FaSearch, FaLightbulb, 
  FaKeyboard, FaBook, FaChartLine, FaUserTie, FaEnvelope, FaGlobe,
  FaInbox, FaPhone, FaUser, FaUserShield, FaRedo, FaBan, FaClock,
  FaTrophy, FaGraduationCap, FaCalendarAlt, FaCreditCard, FaPaperPlane
} from 'react-icons/fa';

const ICON_MAP = {
  FaNewspaper: FaNewspaper,
  FaPencilAlt: FaPencilAlt,
  FaMicrophone: FaMicrophone,
  FaSearch: FaSearch,
  FaLightbulb: FaLightbulb,
  FaKeyboard: FaKeyboard,
  FaBook: FaBook,
  FaChartLine: FaChartLine,
  FaUserTie: FaUserTie
};

const COUNTRIES = [
  "France", "Belgique", "Suisse", "Canada", "Luxembourg", 
  "Maroc", "Tunisie", "Algérie", "Sénégal", "Côte d'Ivoire",
  "Cameroun", "Congo", "Madagascar", "Autre"
];

function SuperAdminDashboard() {
  const [schools, setSchools] = useState([]);
  const [modules, setModules] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [educatorDemos, setEducatorDemos] = useState([]);
  const [concoursStudents, setConcoursStudents] = useState([]);
  const [concoursConfig, setConcoursConfig] = useState({ activeModules: [] });
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showContactsPanel, setShowContactsPanel] = useState(false);
  const [showEducatorPanel, setShowEducatorPanel] = useState(false);
  const [showConcoursPanel, setShowConcoursPanel] = useState(false);
  const [showModuleEditor, setShowModuleEditor] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [newSchool, setNewSchool] = useState({ 
    name: '', 
    logo: '', 
    referentEmail: '', 
    country: 'France',
    activeModules: [] 
  });
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const navigate = useNavigate();
  const user = getUser();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schoolsRes, modulesRes, contactsRes, educatorRes, concoursStudentsRes, concoursConfigRes] = await Promise.all([
        api.get('/superadmin/schools'),
        api.get('/superadmin/modules'),
        api.get('/superadmin/contacts'),
        api.get('/superadmin/educator-demos'),
        api.get('/superadmin/concours-students'),
        api.get('/superadmin/concours-config')
      ]);
      setSchools(schoolsRes.data);
      setModules(modulesRes.data);
      setContacts(contactsRes.data);
      setEducatorDemos(educatorRes.data);
      setConcoursStudents(concoursStudentsRes.data);
      setConcoursConfig(concoursConfigRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchool = async () => {
    try {
      await api.post('/superadmin/schools', newSchool);
      setShowCreateModal(false);
      setNewSchool({ name: '', logo: '', referentEmail: '', country: 'France', activeModules: [] });
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateContactStatus = async (contactId, status) => {
    try {
      await api.put(`/superadmin/contacts/${contactId}?status=${status}`);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleApproveRenewal = async (accountId, days = 7) => {
    try {
      await api.put(`/superadmin/educator-demos/${accountId}/approve-renewal?days=${days}`);
      fetchData();
      alert(`Accès renouvelé pour ${days} jours`);
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeactivateEducator = async (accountId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir désactiver ce compte ?')) return;
    try {
      await api.put(`/superadmin/educator-demos/${accountId}/deactivate`);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateSchool = async (schoolId, updates) => {
    try {
      await api.put(`/superadmin/schools/${schoolId}`, updates);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteSchool = async (schoolId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette école ?')) return;
    try {
      await api.delete(`/superadmin/schools/${schoolId}`);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAIAssist = async () => {
    if (!aiPrompt.trim() || !selectedModule) return;
    setAiLoading(true);
    try {
      const response = await api.post('/superadmin/ai-assist', {
        moduleId: selectedModule.id,
        prompt: aiPrompt,
        context: `Module: ${selectedModule.name} - ${selectedModule.description}`
      });
      setAiResponse(response.data.response);
    } catch (error) {
      setAiResponse('Erreur: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiLoading(false);
    }
  };

  const toggleModuleForSchool = (schoolId, moduleId) => {
    const school = schools.find(s => s.id === schoolId);
    if (!school) return;
    
    const newModules = school.activeModules.includes(moduleId)
      ? school.activeModules.filter(m => m !== moduleId)
      : [...school.activeModules, moduleId];
    
    handleUpdateSchool(schoolId, { activeModules: newModules });
  };

  const toggleConcoursModule = async (moduleId) => {
    const newModules = concoursConfig.activeModules.includes(moduleId)
      ? concoursConfig.activeModules.filter(m => m !== moduleId)
      : [...concoursConfig.activeModules, moduleId];
    
    try {
      await api.put('/superadmin/concours-config', null, {
        params: { activeModules: newModules }
      });
      setConcoursConfig({ ...concoursConfig, activeModules: newModules });
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a2e' }}>
        <div className="onomatopoeia text-white">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#1a1a2e' }}>
      {/* Header */}
      <header className="border-b-4 border-yellow-400" style={{ background: '#16213e' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <FaCog className="text-4xl text-yellow-400 animate-spin" style={{ animationDuration: '10s' }} />
            <div>
              <h1 className="text-2xl md:text-3xl comic-title text-white">Super Admin</h1>
              <p className="text-yellow-400 text-sm">DailyGlobe School Platform</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-white">{user?.email}</span>
            <button onClick={logout} className="comic-button comic-button-red" data-testid="logout-button">
              <FaSignOutAlt />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-center"
            style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' }}
          >
            <FaSchool className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{schools.length}</h3>
            <p className="comic-text text-white text-xl">Écoles</p>
          </motion.div>
          
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-center"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #166534 100%)' }}
          >
            <FaBook className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{modules.length}</h3>
            <p className="comic-text text-white text-xl">Modules</p>
          </motion.div>
          
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            onClick={() => setShowContactsPanel(!showContactsPanel)}
            className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-center cursor-pointer hover:scale-105 transition-transform"
            style={{ background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)' }}
          >
            <FaInbox className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{contacts.filter(c => c.status === 'new').length}</h3>
            <p className="comic-text text-white text-xl">Demandes</p>
          </motion.div>
          
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            onClick={() => setShowEducatorPanel(!showEducatorPanel)}
            className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-center cursor-pointer hover:scale-105 transition-transform"
            style={{ background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)' }}
          >
            <FaUserShield className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{educatorDemos.filter(e => e.renewalRequested).length}</h3>
            <p className="comic-text text-white text-xl">Enseignants</p>
          </motion.div>
          
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35 }}
            onClick={() => setShowConcoursPanel(!showConcoursPanel)}
            className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-center cursor-pointer hover:scale-105 transition-transform"
            style={{ background: 'linear-gradient(135deg, #ea580c 0%, #dc2626 100%)' }}
            data-testid="concours-panel-button"
          >
            <FaTrophy className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{concoursStudents.length}</h3>
            <p className="comic-text text-white text-xl">Concours</p>
          </motion.div>
          
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            onClick={() => window.location.href = '/admin/prospection'}
            className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-center cursor-pointer hover:scale-105 transition-transform"
            style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' }}
            data-testid="prospection-panel-button"
          >
            <FaPaperPlane className="text-5xl mx-auto mb-2 text-white" />
            <p className="comic-text text-white text-xl">Prospection</p>
          </motion.div>
          
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.45 }}
            className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] text-center"
            style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)' }}
          >
            <FaRobot className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">IA</h3>
            <p className="comic-text text-white text-xl">Assistant</p>
          </motion.div>
        </div>

        {/* Concours Panel */}
        <AnimatePresence>
          {showConcoursPanel && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="comic-panel mb-8 overflow-hidden"
              style={{ background: '#f8f4e3' }}
              data-testid="concours-panel"
            >
              <h2 className="comic-title text-2xl mb-4" style={{ color: '#ea580c' }}>
                <FaTrophy className="inline mr-2" /> Prépa Concours Grandes Écoles
              </h2>
              
              {/* Configuration des modules */}
              <div className="mb-6 p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
                <h3 className="font-bold text-lg mb-3 flex items-center">
                  <FaCog className="mr-2" /> Modules actifs pour la prépa concours
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {modules.map(module => {
                    const isActive = concoursConfig.activeModules?.includes(module.id);
                    return (
                      <button
                        key={module.id}
                        onClick={() => toggleConcoursModule(module.id)}
                        className={`p-2 rounded text-sm font-medium transition-all ${
                          isActive 
                            ? 'bg-orange-500 text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {isActive && <FaCheck className="inline mr-1" />}
                        {module.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Liste des étudiants */}
              <h3 className="font-bold text-lg mb-3">
                <FaGraduationCap className="inline mr-2" /> Étudiants inscrits ({concoursStudents.length})
              </h3>
              
              {concoursStudents.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Aucun étudiant inscrit</p>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {concoursStudents.map(student => {
                    const isActive = student.subscriptionStatus === 'active';
                    const isPending = student.subscriptionStatus === 'pending';
                    const isExpired = student.subscriptionStatus === 'expired';
                    
                    return (
                      <div 
                        key={student.id} 
                        className={`p-4 border-2 rounded-lg ${
                          isActive ? 'border-green-400 bg-green-50' :
                          isPending ? 'border-yellow-400 bg-yellow-50' :
                          'border-red-400 bg-red-50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold text-lg">{student.fullName}</h4>
                            <p className="text-sm text-gray-600">
                              <FaEnvelope className="inline mr-1" /> {student.email}
                            </p>
                            {student.examDate && (
                              <p className="text-sm text-orange-600 font-medium">
                                <FaCalendarAlt className="inline mr-1" /> 
                                Examen: {new Date(student.examDate).toLocaleDateString('fr-FR', {
                                  day: 'numeric', month: 'long', year: 'numeric'
                                })}
                              </p>
                            )}
                            {student.targetSchools?.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                <FaGraduationCap className="inline mr-1" /> 
                                {student.targetSchools.join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              isActive ? 'bg-green-200 text-green-800' :
                              isPending ? 'bg-yellow-200 text-yellow-800' :
                              'bg-red-200 text-red-800'
                            }`}>
                              {isActive ? 'Actif' : isPending ? 'En attente' : 'Expiré'}
                            </span>
                            {student.paypalOrderId && (
                              <p className="text-xs text-gray-400 mt-1">
                                <FaCreditCard className="inline mr-1" /> PayPal: {student.paypalOrderId.slice(0, 10)}...
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Educator Demo Panel */}
        <AnimatePresence>
          {showEducatorPanel && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="comic-panel mb-8 overflow-hidden"
              style={{ background: '#f8f4e3' }}
            >
              <h2 className="comic-title text-2xl mb-4" style={{ color: '#0891b2' }}>
                <FaUserShield className="inline mr-2" /> Comptes Démo Enseignants
              </h2>
              
              {educatorDemos.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Aucun compte enseignant</p>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {educatorDemos.map(educator => {
                    const isExpired = new Date(educator.accessExpiresAt) < new Date();
                    const needsRenewal = educator.renewalRequested && !educator.renewalApproved;
                    
                    return (
                      <div 
                        key={educator.id} 
                        className={`p-4 border-2 rounded-lg ${
                          needsRenewal ? 'border-yellow-400 bg-yellow-50' :
                          isExpired ? 'border-red-400 bg-red-50' :
                          educator.isActive ? 'border-green-400 bg-green-50' :
                          'border-gray-300 bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-lg">{educator.fullName}</h3>
                            <p className="text-sm text-gray-600">
                              <FaUserShield className="inline mr-1" /> 
                              {educator.function === 'professor' ? 'Professeur' : 
                               educator.function === 'director' ? 'Directeur' : 
                               educator.function === 'pedagogical_director' ? 'Resp. pédagogique' : 'Autre'}
                              {educator.schoolName && ` - ${educator.schoolName}`}
                            </p>
                            <p className="text-sm text-gray-600">
                              <FaEnvelope className="inline mr-1" /> {educator.email}
                              {educator.phone && <><FaPhone className="inline ml-3 mr-1" /> {educator.phone}</>}
                            </p>
                            <p className="text-xs mt-1 flex items-center">
                              <FaClock className="inline mr-1" />
                              Expire: {new Date(educator.accessExpiresAt).toLocaleDateString('fr-FR')}
                              {isExpired && <span className="ml-2 text-red-600 font-bold">EXPIRÉ</span>}
                              {needsRenewal && <span className="ml-2 text-yellow-600 font-bold">RENOUVELLEMENT DEMANDÉ</span>}
                            </p>
                            {educator.renewalMessage && (
                              <p className="text-sm mt-2 text-gray-700 italic">"{educator.renewalMessage}"</p>
                            )}
                          </div>
                          <div className="flex flex-col space-y-2">
                            {(needsRenewal || isExpired) && (
                              <button
                                onClick={() => handleApproveRenewal(educator.id, 7)}
                                className="px-3 py-1 bg-green-500 text-white rounded text-sm flex items-center"
                              >
                                <FaRedo className="mr-1" /> +7 jours
                              </button>
                            )}
                            {educator.isActive && (
                              <button
                                onClick={() => handleDeactivateEducator(educator.id)}
                                className="px-3 py-1 bg-red-500 text-white rounded text-sm flex items-center"
                              >
                                <FaBan className="mr-1" /> Désactiver
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Contact Requests Panel */}
        <AnimatePresence>
          {showContactsPanel && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="comic-panel mb-8 overflow-hidden"
              style={{ background: '#f8f4e3' }}
            >
              <h2 className="comic-title text-2xl mb-4" style={{ color: '#ea580c' }}>
                <FaInbox className="inline mr-2" /> Demandes de contact
              </h2>
              
              {contacts.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Aucune demande de contact</p>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {contacts.map(contact => (
                    <div 
                      key={contact.id} 
                      className={`p-4 border-2 rounded-lg ${
                        contact.status === 'new' ? 'border-orange-400 bg-orange-50' :
                        contact.status === 'contacted' ? 'border-blue-400 bg-blue-50' :
                        contact.status === 'converted' ? 'border-green-400 bg-green-50' :
                        'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-lg">{contact.schoolName}</h3>
                          <p className="text-sm text-gray-600">
                            <FaUser className="inline mr-1" /> {contact.contactName} ({contact.role})
                          </p>
                          <p className="text-sm text-gray-600">
                            <FaEnvelope className="inline mr-1" /> {contact.email}
                            {contact.phone && <><FaPhone className="inline ml-3 mr-1" /> {contact.phone}</>}
                          </p>
                          {contact.message && (
                            <p className="text-sm mt-2 text-gray-700 italic">"{contact.message}"</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(contact.createdAt).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <select
                            value={contact.status}
                            onChange={(e) => handleUpdateContactStatus(contact.id, e.target.value)}
                            className="text-sm border rounded p-1"
                          >
                            <option value="new">Nouveau</option>
                            <option value="contacted">Contacté</option>
                            <option value="converted">Converti</option>
                            <option value="rejected">Rejeté</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Schools Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="comic-panel mb-8"
          style={{ background: '#f8f4e3' }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="comic-title text-2xl" style={{ color: '#1B4965' }}>
              Gestion des Écoles
            </h2>
            <button
              onClick={() => setShowCreateModal(true)}
              className="comic-button comic-button-navy"
              data-testid="create-school-button"
            >
              <FaPlus className="inline mr-2" /> Nouvelle École
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {schools.map(school => (
              <motion.div
                key={school.id}
                whileHover={{ scale: 1.02 }}
                className={`comic-panel cursor-pointer ${selectedSchool?.id === school.id ? 'border-yellow-400 border-4' : ''}`}
                onClick={() => setSelectedSchool(selectedSchool?.id === school.id ? null : school)}
              >
                <div className="flex items-center space-x-4 mb-4">
                  {school.logo ? (
                    <img src={school.logo} alt={school.name} className="w-16 h-16 object-contain rounded" />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                      <FaSchool className="text-2xl text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="comic-title text-xl">{school.name}</h3>
                    <p className="text-xs text-gray-500 flex items-center">
                      <FaGlobe className="mr-1" /> {school.country || 'France'}
                    </p>
                    <p className={`text-sm ${school.isActive ? 'text-green-600' : 'text-red-600'}`}>
                      {school.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </div>
                
                {school.referentEmail && (
                  <div className="text-xs text-gray-500 mb-2 flex items-center">
                    <FaEnvelope className="mr-1" /> {school.referentEmail}
                  </div>
                )}
                
                <div className="text-sm text-gray-600 mb-4">
                  {school.activeModules.length} modules actifs
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpdateSchool(school.id, { isActive: !school.isActive });
                    }}
                    className={`comic-button text-sm py-1 px-3 ${school.isActive ? 'bg-red-100' : 'bg-green-100'}`}
                  >
                    {school.isActive ? <FaTimes /> : <FaCheck />}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSchool(school.id);
                    }}
                    className="comic-button text-sm py-1 px-3 bg-red-100"
                    data-testid={`delete-school-${school.id}`}
                  >
                    <FaTrash />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Selected School - Module Configuration */}
        <AnimatePresence>
          {selectedSchool && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="comic-panel mb-8 overflow-hidden"
              style={{ background: '#f8f4e3' }}
            >
              <h2 className="comic-title text-2xl mb-6" style={{ color: '#1B4965' }}>
                Modules pour {selectedSchool.name}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {modules.map(module => {
                  const IconComponent = ICON_MAP[module.icon] || FaBook;
                  const isActive = selectedSchool.activeModules.includes(module.id);
                  
                  return (
                    <motion.div
                      key={module.id}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => toggleModuleForSchool(selectedSchool.id, module.id)}
                      className={`comic-panel cursor-pointer p-4 ${isActive ? 'bg-green-100 border-green-500' : 'bg-gray-100'}`}
                    >
                      <div className="flex items-center space-x-3">
                        <IconComponent className="text-2xl" style={{ color: module.color }} />
                        <div className="flex-1">
                          <h4 className="font-bold">{module.name}</h4>
                          <p className="text-xs text-gray-600">{module.description}</p>
                          {module.requiresKeyboard && (
                            <span className="text-xs text-orange-600">Clavier AZERTY requis</span>
                          )}
                        </div>
                        {isActive ? (
                          <FaCheck className="text-green-600" />
                        ) : (
                          <FaTimes className="text-gray-400" />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Module Editor with AI */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="comic-panel"
          style={{ background: '#f8f4e3' }}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="comic-title text-2xl" style={{ color: '#1B4965' }}>
              Éditeur de Modules (Assistant IA)
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Module Selection */}
            <div>
              <h3 className="font-bold mb-4">Sélectionner un module à éditer:</h3>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {modules.map(module => {
                  const IconComponent = ICON_MAP[module.icon] || FaBook;
                  return (
                    <button
                      key={module.id}
                      onClick={() => {
                        setSelectedModule(module);
                        setAiResponse('');
                      }}
                      className={`w-full text-left p-3 rounded border-2 transition-all ${
                        selectedModule?.id === module.id 
                          ? 'border-yellow-400 bg-yellow-50' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <IconComponent style={{ color: module.color }} />
                        <span className="font-medium">{module.name}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* AI Assistant */}
            <div>
              {selectedModule ? (
                <>
                  <h3 className="font-bold mb-4">
                    Assistant IA pour: {selectedModule.name}
                  </h3>
                  <div className="space-y-4">
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="Décrivez ce que vous voulez modifier ou créer...
Ex: Génère 5 nouvelles questions pour ce module sur le thème de la vérification des sources"
                      className="w-full h-32 p-3 border-2 border-black rounded"
                      data-testid="ai-prompt-input"
                    />
                    <button
                      onClick={handleAIAssist}
                      disabled={aiLoading || !aiPrompt.trim()}
                      className="comic-button comic-button-navy w-full"
                      data-testid="ai-assist-button"
                    >
                      <FaRobot className="inline mr-2" />
                      {aiLoading ? 'Génération...' : 'Demander à l\'IA'}
                    </button>
                    
                    {aiResponse && (
                      <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded">
                        <h4 className="font-bold mb-2">Réponse de l'IA:</h4>
                        <pre className="whitespace-pre-wrap text-sm">{aiResponse}</pre>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center text-gray-500 py-12">
                  <FaRobot className="text-6xl mx-auto mb-4 opacity-30" />
                  <p>Sélectionnez un module pour utiliser l'assistant IA</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Create School Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="comic-panel max-w-lg w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="comic-title text-2xl mb-6" style={{ color: '#1B4965' }}>
                Créer une nouvelle école
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block font-bold mb-2">Nom de l'école *</label>
                  <input
                    type="text"
                    value={newSchool.name}
                    onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                    className="w-full p-3 border-2 border-black rounded"
                    placeholder="Ex: ISCPA Lyon"
                    data-testid="new-school-name"
                  />
                </div>
                
                <div>
                  <label className="block font-bold mb-2">URL du logo</label>
                  <input
                    type="text"
                    value={newSchool.logo}
                    onChange={(e) => setNewSchool({ ...newSchool, logo: e.target.value })}
                    className="w-full p-3 border-2 border-black rounded"
                    placeholder="https://..."
                    data-testid="new-school-logo"
                  />
                </div>
                
                <div>
                  <label className="block font-bold mb-2">
                    <FaEnvelope className="inline mr-1" /> Email du référent
                  </label>
                  <input
                    type="email"
                    value={newSchool.referentEmail}
                    onChange={(e) => setNewSchool({ ...newSchool, referentEmail: e.target.value })}
                    className="w-full p-3 border-2 border-black rounded"
                    placeholder="referent@ecole.fr"
                    data-testid="new-school-referent-email"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recevra les annonces de nouvelles fonctionnalités
                  </p>
                </div>
                
                <div>
                  <label className="block font-bold mb-2">
                    <FaGlobe className="inline mr-1" /> Pays
                  </label>
                  <select
                    value={newSchool.country}
                    onChange={(e) => setNewSchool({ ...newSchool, country: e.target.value })}
                    className="w-full p-3 border-2 border-black rounded"
                    data-testid="new-school-country"
                  >
                    {COUNTRIES.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Personnalise les quiz actualité et personnalités
                  </p>
                </div>
                
                <div>
                  <label className="block font-bold mb-2">Modules actifs</label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {modules.map(module => (
                      <label key={module.id} className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newSchool.activeModules.includes(module.id)}
                          onChange={(e) => {
                            const newModules = e.target.checked
                              ? [...newSchool.activeModules, module.id]
                              : newSchool.activeModules.filter(m => m !== module.id);
                            setNewSchool({ ...newSchool, activeModules: newModules });
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{module.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-4 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="comic-button flex-1"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateSchool}
                  disabled={!newSchool.name.trim()}
                  className="comic-button comic-button-navy flex-1"
                  data-testid="confirm-create-school"
                >
                  <FaSave className="inline mr-2" /> Créer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SuperAdminDashboard;
